import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useModals from 'hooks/useModals';
import { useHistory } from 'react-router-dom';
import { LIST_ITEMS_SIZE } from 'config';
import { resolvePlayerNavLink } from 'helpers/urlhelper';
import Grid from 'components/Grid';
import ListItem from 'components/ListItem';
import EmptyDataStub from 'components/EmptyDataStub';
import WatchListIcon from 'components/Icons/menu-volia/watchlist';
import PLAYER_TYPES from 'constants/player-types';
import { TabContentWrapper } from './StyledComponents';
import { navigateToPlayerPage } from 'actions/player';
import { get } from 'lodash';
import isPgPinNeeded from 'utils/is-pg-pin-needed';

import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import {
  selectParentalControlAge,
  selectIsParentalControlEnabled,
} from 'reducers/parental-lock/selectors';
import { CONTENT_TYPES } from 'constants/content-types';

const TabContent = ({ content, type, emptyPlaceholder, loadExtraItems, removeItem }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isParentalEnabled = useSelector(selectIsParentalControlEnabled);
  const age = useSelector(selectParentalControlAge);

  const ROW_SIZE = 3;

  const { showEnterPinModal } = useModals();

  const onEnter = useCallback(
    (item) => {
      const vodPlayerType = get(item, ['type'], 'VOD');
      const goToPlayer = () =>
        dispatch(
          navigateToPlayerPage({
            item,
            type: PLAYER_TYPES[['channel', 'catchup'].includes(type) ? 'CHANNEL' : vodPlayerType],
          }),
        );
      //Если это VOD(фильм или сериал), то отправляем на страницу деталей либо для просмотра либо для покупки по SVOD, TVOD, EST
      if (type === 'vod') {
        const isSeries =
          type === 'vod' && get(item, ['type'], 'VOD_ASSET') === CONTENT_TYPES.TV_SERIES;
        const assetContentType =
          type === 'vod' && isSeries ? CONTENT_TYPES.TV_SERIES : CONTENT_TYPES.VOD_ASSET;

        history.push(`/details/${assetContentType}/${item.id}`);
        return;
      }

      if (item.visibilityDetails === 'MISSING_SUBSCRIPTION') {
        //Если это недоступный кечап или канал, то просим купить в списках, т.к. у них нет отедльной страницы
        const channelData = type === 'channel' ? `content_type=CHANNEL; channel_id=${item.id}` : '';
        const vodData =
          type === 'vod'
            ? `vod_asset_id=${item.id}; content_type=${get(
                item,
                'metadata.contentType',
                item.type,
              )}`
            : '';
        const catchupData =
          type === 'catchup'
            ? `vod_asset_id=${get(item, ['epgEvent', 'id'], item.id)}; content_type=${get(
                item,
                ['epgEvent', 'state'],
                item.type,
              )}; channel_id=${get(item, ['epgEvent', 'channel', 'id'], item.id)}`
            : '';
        const addInfo = vodData || channelData || catchupData;

        innerAnalyticsVolia.postEvent({
          eventGroup: 'notification',
          eventName: 'unavailable content',
          eventValue: 'show notification',
          addInfo,
        });

        const onSuccess = resolvePlayerNavLink(item, {
          type: PLAYER_TYPES[['channel', 'catchup'].includes(type) ? 'CHANNEL' : vodPlayerType],
        });

        history.push({
          pathname: '/profile/packages/filter',
          search: `?${['channel', 'catchup'].includes(type) ? 'channelId' : 'vodAssetId'}=${
            item.id
          }&onSuccess=${onSuccess}`,
        });
        return;
      }

      //Если включен РК для каналов ???
      // Проверить
      if (isParentalEnabled) {
        const isAdultChannel = get(item, ['pgRating', 'minRecomendedAge'], 0) === 18;
        const isPinNeeded = isPgPinNeeded(item.pgRating, age);

        if (isPinNeeded || isAdultChannel) {
          innerAnalyticsVolia.postEvent({
            eventGroup: 'notification',
            eventName: 'PIN request',
            eventValue: 'show notification',
          });

          showEnterPinModal({ sectionToFocus: `@GRID` }, goToPlayer);
          return;
        }
      }
      goToPlayer();
    },

    [type, isParentalEnabled, dispatch, history, age, showEnterPinModal],
  );

  const onFocus = useCallback(
    (_item, index) => {
      const rows = content.data.length / ROW_SIZE;
      const currentRowIndex = Math.ceil(index / ROW_SIZE);
      const isRequiresExtraLoad = currentRowIndex >= rows - 1 - 1;
      if (isRequiresExtraLoad) loadExtraItems(type);
    },
    [content, loadExtraItems, type],
  );

  const dashboardName = type === 'vod' ? 'MY_WATCHLIST' : 'CHANNELS_FAVORITES';

  const onEdit = useCallback(
    (item) => removeItem({ type, item, dashboardName }),
    [dashboardName, removeItem, type],
  );

  return (
    <TabContentWrapper>
      {!content.isLoading && (
        <Grid
          items={content.data || []}
          rowSize={ROW_SIZE}
          showProgress={false}
          isEditable={true}
          gridId={`grid-continue-watching-${type}`}
          key={`grid-continue-watching-${type}`}
          onEditItem={onEdit}
          onEnter={onEnter}
          onFocus={onFocus}
          component={ListItem}
          itemSize={LIST_ITEMS_SIZE['LARGE']}
          dashboardName={dashboardName}
          withoutContent={
            <EmptyDataStub
              emptyPlaceholder={emptyPlaceholder}
              icon={<WatchListIcon width={192} height={192} />}
            />
          }
        />
      )}
    </TabContentWrapper>
  );
};

TabContent.defaultProps = {
  content: {},
};

TabContent.propsTypes = {
  content: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
    data: PropTypes.array.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
  emptyPlaceholder: PropTypes.string.isRequired,
  loadExtraItems: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
};

export default TabContent;
